import React from "react";
import { Card } from "../ui/Card";

export interface ResourceTabProps<TResource> {
  // Resource data from hook
  resource: {
    data: TResource | undefined;
    isLoading: boolean;
    isError: boolean;
    // Optional pagination props - only needed if table uses pagination
    page?: number;
    pageSize?: number;
    handlePageChange?: (page: number) => void;
    handlePageSizeChange?: (size: number) => void;
  };

  // Filters component
  filtersComponent: React.ReactNode;

  // Table component
  tableComponent: React.ReactNode;

  /** Optional action (e.g. "Nuevo" button) rendered in the top right of the card */
  headerAction?: React.ReactNode;

  // Empty state
  emptyStateMessage: string;

  // Edit modal
  editModal?: React.ReactNode;

  // Create modal
  createModal?: React.ReactNode;
}

/**
 * ResourceTab
 * A generic tab component for displaying resources (buildings, users, etc.)
 * with filters, table, and create/edit modals.
 */
export function ResourceTab<TResource>({
  resource,
  filtersComponent,
  tableComponent,
  headerAction,
  emptyStateMessage,
  editModal,
  createModal,
}: ResourceTabProps<TResource>) {
  // Check if data is empty
  // Handle both undefined data and PaginatedResponse with empty items
  const isEmpty =
    !resource.isLoading &&
    !resource.isError &&
    (!resource.data ||
      (resource.data &&
        typeof resource.data === "object" &&
        "total" in resource.data &&
        (resource.data as { total: number }).total === 0));

  return (
    <Card className="p-4 sm:p-5 relative">
      {/* Header action (e.g. "Nuevo" button) in the top right */}
      {headerAction && (
        <div className="flex justify-end mb-3">{headerAction}</div>
      )}

      {/* Filters */}
      {filtersComponent}

      {/* Table */}
      {isEmpty ? (
        <div className="text-gray-500 text-sm text-center">
          {emptyStateMessage}
        </div>
      ) : (
        tableComponent
      )}

      {/* Edit modal */}
      {editModal}

      {/* Create modal */}
      {createModal}
    </Card>
  );
}
